import React, { memo, useMemo } from "react";

import { Checkbox } from "antd";
import Highlighter from "react-highlight-words";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";

import { getProductDisplayData } from "../../utils";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as AddFillIcon } from "../../../../../assets/images/product/specmanager/add-fill.svg";

import "./ProductList.css";
import "./AllProductList.css";

function renderHighlightedValue(searchTerm, value) {
  const text = value != null ? String(value) : "";
  const term = searchTerm && String(searchTerm).trim();
  if (!term) return text;
  return (
    <Highlighter
      highlightClassName="searchHighlight"
      searchWords={[term]}
      autoEscape
      textToHighlight={text}
    />
  );
}

function AllProductListItem({
  product,
  fields,
  linkReportJsonFields,
  searchTerm,
  checked,
  alreadySelected,
  onToggleChecked,
  onAddProduct,
}) {
  const { titleToValue, fullText } = getProductDisplayData(product, fields, linkReportJsonFields);
  const entries = Object.entries(titleToValue);

  return (
    <StyledTooltip
      placement="left"
      title={fullText}
      className="SpecManagerProductTooltip"
      getPopupContainer={(trigger) => trigger?.closest(".ant-drawer-body") || trigger?.closest(".ant-modal-body") || document.body}
    >
      <li
        className={`ProductList__SelectedListItem AllProductList__Row${alreadySelected ? " AllProductList__Row--inSelectedList" : ""}`}
        onClick={() => {
          if (alreadySelected || !onToggleChecked) return;
          onToggleChecked(product, !checked);
        }}
      >
        <div className="ProductList__SelectedItemLeft">
          <div
            className="AllProductList__CheckboxStopPropagation"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={checked}
              disabled={alreadySelected}
              className="ProductList__Checkbox"
              onChange={(e) => {
                if (onToggleChecked) onToggleChecked(product, e.target.checked);
              }}
            />
          </div>
          <span className="ProductList__SelectedItemText">
            {entries.length > 0
              ? entries.map(([k, v], i) => (
                <React.Fragment key={k}>
                  {i > 0 && " \u2022 "}
                  <strong>
                    {k}
                    :
                  </strong>
                  {" "}
                  {renderHighlightedValue(searchTerm, v)}
                </React.Fragment>
              ))
              : null}
          </span>
        </div>
        <div className="ProductList__SelectedItemRight">
          <div
            className={`AllProductList__AddIcon${alreadySelected ? " AllProductList__AddIcon--disabled" : ""}`}
            onClick={
              alreadySelected
                ? undefined
                : (e) => {
                  e.stopPropagation();
                  if (onAddProduct) onAddProduct(product);
                }
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !alreadySelected && onAddProduct) onAddProduct(product);
            }}
            role="button"
            tabIndex={alreadySelected ? -1 : 0}
            aria-label={alreadySelected ? "Product already selected" : "Add product"}
            aria-disabled={alreadySelected || undefined}
          >
            <AddFillIcon width={20} height={20} />
          </div>
        </div>
      </li>
    </StyledTooltip>
  );
}

const MemoizedAllProductListItem = memo(AllProductListItem);

/**
 * All Products list for stacked layout: checkbox + text + add control (no spec-type badges
 * like the Selected list). Checkbox selection drives "Add Products (n)" in footer.
 */
function AllProductList(props) {
  const {
    products,
    searchTerm = "",
    loading,
    checkedProductIds,
    onToggleRowChecked,
    onAddProduct,
    selectedProductIds,
    fields = [],
    linkReportJsonFields = [],
  } = props;

  const selectedIds = useMemo(
    () => (selectedProductIds instanceof Set ? selectedProductIds : new Set(selectedProductIds ?? [])),
    [selectedProductIds],
  );

  if (loading) {
    return (
      <div
        className="AllProductList__ListWrap AllProductList__ListWrap--loading"
        role="status"
        aria-label="Loading products"
      >
        <div className="AllProductList__Loading">
          <LoadingDots width={28} height={28} fill="#19305A" />
        </div>
      </div>
    );
  }

  const isEmpty = !products || products.length === 0;

  if (isEmpty) {
    return (
      <div className="AllProductList__ListWrap">
        <div className="ProductList__EmptyState">
          <h4 className="ProductList__EmptyStateTitle">No products found in the All Products list.</h4>
          <h2 className="ProductList__EmptySubTitle">The product might not exist.</h2>
        </div>
      </div>
    );
  }

  const checkedIdSet = checkedProductIds ?? new Set();

  return (
    <StyledScrollbar id="AllProductList__ScrollBar" className="AllProductList__Scrollbar">
      <ul className="AllProductList__List">
        {products.map((product) => (
          <MemoizedAllProductListItem
            key={product.product_id}
            product={product}
            fields={fields}
            linkReportJsonFields={linkReportJsonFields}
            searchTerm={searchTerm}
            checked={checkedIdSet.has(product.product_id)}
            alreadySelected={selectedIds.has(product.product_id)}
            onToggleChecked={onToggleRowChecked}
            onAddProduct={onAddProduct}
          />
        ))}
      </ul>
    </StyledScrollbar>
  );
}

export default memo(AllProductList);
